import { useMemo } from 'react'
import { Text, View } from 'react-native'
import type { ImageSourcePropType } from 'react-native'
import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { Lock } from 'lucide-react-native'
import { useTranslation } from 'react-i18next'
import { Controller, useWatch } from 'react-hook-form'
import type { Control } from 'react-hook-form'
import { colors } from '@chillout/core'
import { SelectField } from '../../shared/SelectField'
import type { SelectFieldOption } from '../../shared/SelectField'
import { IconPickerField } from './IconPickerField'
import type { CardIconKey } from '@chillout/ui'
import type { LoyaltyConfig, RewardItem } from './useLoyaltyConfig'

type MilestoneRow =
  | {
      isCompletion: false
      fieldName: `milestones.${number}.rewardItemId`
      cardIconFieldName: `milestones.${number}.cardIcon`
      stampPosition: number
      rewardItemId: string
      cardIcon: CardIconKey
    }
  | {
      isCompletion: true
      fieldName: 'completionRewardItemId'
      cardIconFieldName: 'completionCardIcon'
      rewardItemId: string
      cardIcon: CardIconKey
    }

interface MilestonesSectionProps {
  control: Control<LoyaltyConfig>
  rewardItems: RewardItem[]
  stepNumber: number
}

export function MilestonesSection({ control, rewardItems, stepNumber }: MilestonesSectionProps) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language === 'sv' ? 'sv' : 'en'
  const milestones = useWatch({ control, name: 'milestones' })
  const completionRewardItemId = useWatch({ control, name: 'completionRewardItemId' })
  const completionCardIcon = useWatch({ control, name: 'completionCardIcon' })

  const rewardItemOptions = useMemo<SelectFieldOption[]>(
    () =>
      rewardItems
        .filter((r) => r.active)
        .map((r) => ({
          label: r.name[lang] || r.name.en,
          value: r.id,
          imageSource: r.imageUrl ? ({ uri: r.imageUrl } as ImageSourcePropType) : undefined,
        })),
    [rewardItems, lang],
  )

  const tableData = useMemo<MilestoneRow[]>(() => {
    const sortedIndexed = milestones
      .map((m, index) => ({ m, index }))
      .sort((a, b) => a.m.stampPosition - b.m.stampPosition)

    return [
      ...sortedIndexed.map(({ m, index }) => ({
        isCompletion: false as const,
        fieldName: `milestones.${index}.rewardItemId` as const,
        cardIconFieldName: `milestones.${index}.cardIcon` as const,
        stampPosition: m.stampPosition,
        rewardItemId: m.rewardItemId,
        cardIcon: m.cardIcon,
      })),
      {
        isCompletion: true as const,
        fieldName: 'completionRewardItemId' as const,
        cardIconFieldName: 'completionCardIcon' as const,
        rewardItemId: completionRewardItemId,
        cardIcon: completionCardIcon,
      },
    ]
  }, [milestones, completionRewardItemId, completionCardIcon])

  const columns = useMemo<ColumnDef<MilestoneRow>[]>(
    () => [
      {
        id: 'position',
        size: 128,
        header: () => t('loyalty.milestones.stampPosition'),
        cell: ({ row }) =>
          row.original.isCompletion ? (
            <View className="h-14 xl:h-12 w-full flex-row items-center justify-between rounded-md border border-border bg-canvas-primary px-3">
              <Text className="flex-1 text-sm text-secondary" numberOfLines={1}>{t('loyalty.milestones.completion')}</Text>
              <Lock size={14} color={colors.text.muted} strokeWidth={1.8} />
            </View>
          ) : (
            <View className="h-14 xl:h-12 w-full flex-row items-center rounded-md border border-border bg-canvas-card px-3">
              <Text className="text-sm text-primary">{row.original.stampPosition}</Text>
            </View>
          ),
      },
      {
        id: 'rewardItem',
        header: () => t('loyalty.milestones.rewardItem'),
        cell: ({ row }) => {
          const original = row.original
          const position = original.isCompletion ? null : original.stampPosition
          return (
            <Controller
              control={control}
              name={original.fieldName}
              render={({ field, fieldState }) => (
                <SelectField
                  value={field.value}
                  options={rewardItemOptions}
                  error={fieldState.error?.message}
                  emptyText={t('loyalty.milestones.noRewardItems')}
                  accessibilityLabel={
                    position === null
                      ? t('loyalty.milestones.completionRewardAccessibilityLabel')
                      : t('loyalty.milestones.rewardItemAccessibilityLabel', { position })
                  }
                  onValueChange={field.onChange}
                />
              )}
            />
          )
        },
      },
      {
        id: 'cardIcon',
        size: 96,
        header: () => t('loyalty.milestones.cardIcon'),
        cell: ({ row }) => {
          const original = row.original
          const position = original.isCompletion ? null : original.stampPosition
          return (
            <Controller
              control={control}
              name={original.cardIconFieldName}
              render={({ field }) => (
                <IconPickerField
                  value={field.value}
                  onValueChange={field.onChange}
                  accessibilityLabel={
                    position === null
                      ? t('loyalty.milestones.completionCardIconAccessibilityLabel')
                      : t('loyalty.milestones.cardIconAccessibilityLabel', { position })
                  }
                />
              )}
            />
          )
        },
      },
    ],
    [t, control, rewardItemOptions],
  )

  const table = useReactTable({ data: tableData, columns, getCoreRowModel: getCoreRowModel() })

  return (
    <View className="gap-5 rounded-xl border border-border bg-canvas-card p-6">
      <View>
        <View className="flex-row items-baseline gap-2">
          <Text className="text-lg font-semibold text-primary">{stepNumber}.</Text>
          <Text className="text-lg font-semibold text-primary">{t('loyalty.milestones.sectionTitle')}</Text>
        </View>
        <Text className="mt-1 text-sm text-secondary">{t('loyalty.milestones.subtitle')}</Text>
      </View>

      <View className="gap-3">
        {table.getHeaderGroups().map((headerGroup) => (
          <View key={headerGroup.id} className="flex-row items-center gap-3">
            {headerGroup.headers.map((header) => (
              <View
                key={header.id}
                style={
                  header.column.id === 'rewardItem'
                    ? { flex: 1, minWidth: 88 }
                    : header.column.id === 'position'
                      ? { flex: 1, maxWidth: 120 }
                      : { flex: 1, maxWidth: header.getSize() }
                }
              >
                <Text className="text-xs font-semibold uppercase tracking-wide text-secondary" numberOfLines={1}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </Text>
              </View>
            ))}
          </View>
        ))}

        {table.getRowModel().rows.map((row) => (
          <View key={row.id} className="flex-row items-center gap-3">
            {row.getVisibleCells().map((cell) => (
              <View
                key={cell.id}
                style={
                  cell.column.id === 'rewardItem'
                    ? { flex: 1, minWidth: 88 }
                    : cell.column.id === 'position'
                      ? { flex: 1, maxWidth: 120 }
                      : { flex: 1, maxWidth: cell.column.getSize() }
                }
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </View>
            ))}
          </View>
        ))}
      </View>

    </View>
  )
}